// elicitationAnswerMapper — the pure boundary map from the UI's `ChatFieldAnswer`
// sum to the domain's `Conversation.Elicitation.ElicitationAnswer` (LANE S3).
//
// The ElicitationForm submit crosses from the impure shell into the pure
// domain command `supply_information`: the shell collects a `ChatFieldAnswer[]`
// (what the form's controls produced) and the domain wants an
// `ElicitationAnswer` (a `FieldAnswer[]` built through the smart ctors, with
// each `key` branded as a `FieldKey`). This module is the ONE place that
// translation happens — per-variant, via the domain ctors, so no `as` cast is
// needed and a new UI answer variant breaks the switch until it is handled.
//
// Pure (no I/O, no mutation): it belongs to `app` as a shell-boundary helper,
// not to `core` (it speaks a UI type) and not inside a component (it is not
// presentation). AppShellHost calls it to build the command it hands the port.

import * as Core from "@bilda/core";
import { exhaustSwitchCase } from "@bilda/utils";
import {
    type AnsweredFieldSummary,
    type ChatField,
    type ChatFieldAnswer,
    FALLBACK_FIELD_KEY,
    type InformationRequestEntry,
    OWN_ANSWER_FIELD_KEY,
} from "../components/Chat/types.js";

const El = Core.Conversation.Elicitation;

// Map ONE UI answer to its domain twin. Each branch calls the matching answer
// ctor and brands the raw `key` string through `mkFieldKey` — the correlation
// back to the field it satisfies.
const toFieldAnswer = (
    answer: ChatFieldAnswer,
): Core.Conversation.Elicitation.FieldAnswer => {
    switch (answer.type) {
        case "answer_text":
            return El.mkTextAnswer(El.mkFieldKey(answer.key), answer.value);
        case "answer_single_choice":
            return El.mkSingleChoiceAnswer(
                El.mkFieldKey(answer.key),
                answer.value,
            );
        case "answer_multi_choice":
            return El.mkMultiChoiceAnswer(
                El.mkFieldKey(answer.key),
                answer.values,
            );
        case "answer_scale":
            return El.mkScaleAnswer(El.mkFieldKey(answer.key), answer.value);
        case "answer_sources":
            return El.mkSourcesAnswer(
                El.mkFieldKey(answer.key),
                answer.sources.map((s) => El.mkSourcePick(s.id, s.title)),
            );
        default:
            return exhaustSwitchCase(answer);
    }
};

// The full submission: every UI answer mapped, wrapped in the domain
// `ElicitationAnswer` the `supply_information` command carries.
export const toElicitationAnswer = (
    answers: readonly ChatFieldAnswer[],
): Core.Conversation.Elicitation.ElicitationAnswer =>
    El.mkElicitationAnswer(answers.map(toFieldAnswer));

// === Resume text (F12) ========================================================
// After the Author answers an ElicitationForm, the turn must RESUME — the agent
// continues with the answer and streams its next message. The agent sees the turn
// as a fresh user message (elicitation answers live in the artifact accumulator,
// which `buildInitialMessages`/`priorTurns` does not surface to the model), so the
// resume turn carries the answers rendered as readable text. This is that
// rendering: one `Label: value` line per answered field, mapping choice VALUES
// back to their option LABELS via the request's fields, so the line reads as what
// the Author chose (not an opaque slug) both to the agent and in the echoed
// author bubble. Pure — a fold over the answers with a per-field lookup.

// Index every field across all steps by its key, for value→label resolution.
const fieldsByKey = (
    entry: InformationRequestEntry,
): ReadonlyMap<string, ChatField> =>
    new Map(
        entry.steps.flatMap((step) =>
            step.fields.map((field) => [field.key, field] as const),
        ),
    );

// Map one choice VALUE to its option LABEL when the field carries options;
// otherwise pass the value through (a value with no matching option is still
// honest text).
const optionLabel = (field: ChatField | undefined, value: string): string => {
    if (
        field?.type === "field_single_choice" ||
        field?.type === "field_multi_choice"
    ) {
        return field.options.find((o) => o.value === value)?.label ?? value;
    }
    return value;
};

// Render ONE answer to its readable value text (no label prefix).
const answerValueText = (
    field: ChatField | undefined,
    answer: ChatFieldAnswer,
): string => {
    switch (answer.type) {
        case "answer_text":
            return answer.value;
        case "answer_single_choice":
            return optionLabel(field, answer.value);
        case "answer_multi_choice":
            return answer.values.map((v) => optionLabel(field, v)).join(", ");
        // A scale answer in words when the scale names its levels.
        case "answer_scale":
            return field?.type === "field_scale"
                ? (field.levels?.[answer.value - field.min]?.label ??
                      String(answer.value))
                : String(answer.value);
        case "answer_sources":
            return answer.sources.map((s) => s.title).join(", ");
        default:
            return exhaustSwitchCase(answer);
    }
};

// The structured field→answer read-back (F97): one `{ key, label, display }`
// per answer that carried a value (a blank field is dropped, matching the resume
// text). Choice VALUES are mapped back to their option LABELS via the request's
// fields, so `display` reads as what the Author chose (not an opaque slug). Pure
// — the ONE place the answered-form summary is built, shared by the resume-text
// renderer below AND the answered-submission projection (conversationAdapter),
// so the read-back the agent sees and the read-back the card shows never drift.
export const summarizeElicitationAnswers = (
    entry: InformationRequestEntry,
    answers: readonly ChatFieldAnswer[],
): readonly AnsweredFieldSummary[] => {
    const byKey = fieldsByKey(entry);
    return answers
        .map((answer) => {
            const field = byKey.get(answer.key);
            const display = answerValueText(field, answer);
            if (display.trim() === "") return undefined;
            return {
                key: answer.key,
                label: field?.label ?? answer.key,
                display,
            };
        })
        .filter((s): s is AnsweredFieldSummary => s !== undefined);
};

// The resume turn's text when nothing carried a value (all fields blank, or the
// request entry is gone). Agent-facing and LOCALE-FREE (like the opening-move
// seed): it is never shown — the reload projection hides the resume message —
// so it must not drift with the UI locale (F113).
export const FORM_RESUME_FALLBACK_TEXT =
    "(The Author has answered your form. Please continue.)";

// The Author's answers as the resume turn's user text: one `Label: value` line
// per answered field, or `FORM_RESUME_FALLBACK_TEXT` when nothing carried a
// value, so the turn always resumes with a real instruction. Built on the
// shared summary so the resume text and the card's read-back stay in lockstep.
export const renderElicitationResumeText = (
    entry: InformationRequestEntry,
    answers: readonly ChatFieldAnswer[],
): string =>
    summarizeElicitationAnswers(entry, answers)
        .map((s) =>
            // Reserved-key fields (the free-text escape / durable fallback) carry
            // a synthesized inert-English label — render the author's words ALONE
            // (no `Label:` prefix) so no English leaks into the echoed author
            // bubble, and the agent still reads the free-text answer verbatim.
            s.key === OWN_ANSWER_FIELD_KEY || s.key === FALLBACK_FIELD_KEY
                ? s.display
                : `${s.label}: ${s.display}`,
        )
        .join("\n") || FORM_RESUME_FALLBACK_TEXT;
